<template>
  <div v-if="isLoading" class="text-center">
    <p>Chargement en cours...</p>
  </div>
  <div v-else class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">Modifier un professeur</h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="professeurForm" @submit="editProfesseur" :validation-schema="professeurSchema"
        :initial-values="initialValues">
        <div class="row">
          <!-- Nom -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Nom du professeur <span class="text-danger">*</span>
              </label>
              <Field name="nom" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le nom" />
              <ErrorMessage name="nom" class="text-danger" />
            </div>
          </div>

          <!-- Prénom -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Prénom du professeur <span class="text-danger">*</span>
              </label>
              <Field name="prenom" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le prenom" />
              <ErrorMessage name="prenom" class="text-danger" />
            </div>
          </div>

          <!-- NPI -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Numéro NPI
              </label>
              <Field name="npi" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le NPI" />
              <ErrorMessage name="npi" class="text-danger" />
            </div>
          </div>

          <!-- IFU -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                IFU
              </label>
              <Field name="ifu" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer l'ifu" />
              <ErrorMessage name="ifu" class="text-danger" />
            </div>
          </div>

          <!-- Email -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Email <span class="text-danger">*</span>
              </label>
              <Field name="email" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer l'email" />
              <ErrorMessage name="email" class="text-danger" />
            </div>
          </div>

          <!-- Sexe -->
          <div class="col-md-4 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Sexe <span class="text-danger">*</span>
              </label>
              <Field name="sexe" type="text" v-slot="{ field }">
                <Multiselect :searchable="true" :options="['Masculin', 'Féminin']" v-model="field.value" v-bind="field"
                  placeholder="Sélectionner le sexe" />
              </Field>
              <ErrorMessage name="sexe" class="text-danger" />
            </div>
          </div>

          <!-- Téléphone 1 -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Téléphone 1 <span class="text-danger">*</span>
              </label>
              <Field name="telProfesseur1" v-slot="{ field }">
                <vue-tel-input :value="field.value || ''" @input="field.onChange($event)" defaultCountry="BJ"
                  mode="international" @validate="validate" class="shadow-none fs-md-15 text-black py-2"
                  placeholder="Entrer le numéro de téléphone" />
              </Field>
              <div v-if="!validPhone" class="text-danger">Veuillez entrer un numéro correct</div>
              <ErrorMessage name="telProfesseur1" class="text-danger" />
            </div>
          </div>

          <!-- Téléphone 2 -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Téléphone 2
              </label>
              <Field name="telProfesseur2" v-slot="{ field }">
                <vue-tel-input :value="field.value || ''" @input="field.onChange($event)" defaultCountry="BJ"
                  mode="international" @validate="validate" class="shadow-none fs-md-15 text-black py-2"
                  placeholder="Entrer le numéro de téléphone" />
              </Field>
              <ErrorMessage name="telProfesseur2" class="text-danger" />
            </div>
          </div>

          <!-- Date de naissance -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de naissance
              </label>
              <Field name="dateNaissance" type="date" :max="currentDate"
                class="form-control shadow-none fs-md-15 text-black" />
              <ErrorMessage name="dateNaissance" class="text-danger" />
            </div>
          </div>

          <!-- Nationalité -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Pays de naissance <span class="text-danger">*</span>
              </label>
              <Field name="nationalite" v-slot="{ field }">
                <Multiselect :options="countries" :searchable="true" track-by="value" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner le pays de naissance" />
              </Field>
              <ErrorMessage name="nationalite" class="text-danger" />
            </div>
          </div>

          <!-- Commune -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Commune <span class="text-danger">*</span>
              </label>
              <Field name="commune">
                <Multiselect :options="communeOptions" v-model="selectedCommune" @change="communeChange"
                  track-by="value" label="label" placeholder="Sélectionner la commune" />
              </Field>

              <ErrorMessage name="commune" class="text-danger" />
            </div>
          </div>

          <!-- Quartier -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Quartier <span class="text-danger">*</span>
              </label>
              <Field name="quartier">
                <Multiselect :options="quartierOptions" v-model="selectedQuartier" track-by="value" label="label"
                  placeholder="Sélectionner le quartier" noOptionsText="Sélectionner d'abord une commune" />
              </Field>

              <ErrorMessage name="quartier" class="text-danger" />
            </div>
          </div>

          <!-- Années d'expérience -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Nombre d'années d'expériences
              </label>
              <Field name="anneeExperience" type="number" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le nombre" />
              <ErrorMessage name="anneeExperience" class="text-danger" />
            </div>
          </div>

          <!-- Matières -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Matières <span class="text-danger">*</span>
              </label>
              <Field name="matieres" v-slot="{ field }">
                <Multiselect :options="matieresOptions" :searchable="true" mode="tags" track-by="label" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner les matières" />

              </Field>
              <ErrorMessage name="matieres" class="text-danger" />
            </div>
          </div>

          <!-- Qualification -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Qualification <span class="text-danger">*</span>
              </label>
              <Field name="qualification" v-slot="{ field }">
                <Multiselect :options="qualificationOptions" :searchable="true" track-by="label" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner la qualification" />
              </Field>
              <ErrorMessage name="qualification" class="text-danger" />
            </div>
          </div>
          <!-- Activités -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Activité <span class="text-danger">*</span>
              </label>
              <Field name="activites" v-slot="{ field }">
                <Multiselect :options="activiteOptions" :searchable="true" mode="tags" track-by="label" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner l'activité" />
              </Field>
              <ErrorMessage name="activites" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Langue <span class="text-danger">*</span>
              </label>
              <Field name="langues" v-slot="{ field }">
                <Multiselect :options="languesOptions" :searchable="true" mode="tags" track-by="label" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner les langues" />
              </Field>
            </div>
            <ErrorMessage name="langues" class="text-danger" />
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Poste occupé actuellement
              </label>
              <Field name="poste" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le poste" />
              <ErrorMessage name="poste" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Catégorie Profesionnelle
              </label>
              <Field name="categorie" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer la categorie" />
              <ErrorMessage name="categorie" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Informations complémentaires <span class="text-danger">*</span>
              </label>
              <Field name="biographie" rows="2" as="textarea" placeholder="Ajouter d'autres informations"
                v-slot="{ field }" class="form-control shadow-none rounded-0 text-black">
                <textarea v-model="field.value" class="form-control shadow-none rounded-0 text-black"></textarea>
              </Field>
              <ErrorMessage name="biographie" class="text-danger" />
            </div>
          </div>

          <!-- Boutons -->
          <div class="col-md-12 mt-3">
            <div class="d-flex align-items-center">
              <button ref="submitButton" class="btn btn-success me-3" type="submit">
                Modifier le professeur
              </button>
              <router-link to="/liste-professeurs" class="btn btn-danger">
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useRouter, useRoute } from 'vue-router';
import ApiService from '@/services/ApiService';
import * as Yup from 'yup';
import Multiselect from '@vueform/multiselect';
import { VueTelInput } from 'vue3-tel-input';
import { success, error } from '@/utils/utils';
import { countries } from '../../components/Inscription/countries';
import axios from 'axios';

export default defineComponent({
  name: 'EditProfesseur',
  components: { Form, Field, ErrorMessage, Multiselect, VueTelInput },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isLoading = ref(true);
    const currentDate = ref(new Date().toISOString().split('T')[0]);
    const submitButton = ref<HTMLButtonElement | null>(null);
    const validPhone = ref<boolean>(true);
    const initialValues = ref<any>({});
    const nationalite = ref('Bénin');
    const matieresOptions = ref([]);
    const languesOptions = ref([]);

    const qualificationOptions = ref([]);
    const activiteOptions = ref([]);
    const communeOptions = ref([]);
    const quartierOptions = ref([]);
    const selectedCommune = ref<number | null>(null);
    const selectedQuartier = ref<number | null>(null);


    const professeurSchema = Yup.object().shape({
      nom: Yup.string().required('Le nom du professeur est obligatoire'),
      prenom: Yup.string().required('Le prénom du professeur est obligatoire'),
      sexe: Yup.string().required('Le sexe est obligatoire'),
      npi: Yup.string()
        .matches(/^\d{10}$/, 'La saisie doit avoir exactement 10 chiffres')
        .notRequired(),
      ifu: Yup.string()
        .matches(/^\d{13}$/, 'La saisie doit avoir exactement 13 chiffres')
        .notRequired(),
      email: Yup.string().email('Veuillez entrer un mail valide').notRequired(),
      telProfesseur1: Yup.string().required('Le téléphone 1 est obligatoire'),
      telProfesseur2: Yup.string().notRequired(),
      dateNaissance: Yup.date()
        .notRequired()
        .max(new Date(), 'La date de naissance ne peut pas être dans le futur')
        .test(
          'age-minimum',
          'Le professeur doit avoir au moins 18 ans',
          function (value) {
            if (!value) return false;
            const today = new Date();
            const birthDate = new Date(value);
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            return age >= 18;
          }
        ),
      biographie: Yup.string().required("La biographie est obligatoire"),
      categorie: Yup.string()
        .notRequired(),
      poste: Yup.string()
        .notRequired(),
      langues: Yup.array()
        .of(Yup.string().required())
        .min(1, 'Au moins une langue est obligatoire'),
      nationalite: Yup.string().required('La nationalité est obligatoire'),
      commune: Yup.number().required('La commune est obligatoire'),
      quartier: Yup.number().required('Le quartier est obligatoire'),
      anneeExperience: Yup.number().notRequired(),
      matieres: Yup.array()
        .of(Yup.string().required())
        .min(1, 'Au moins une matière est obligatoire'),
      qualification: Yup.number().required('La qualification est obligatoire'),
      activites: Yup.array()
        .of(Yup.string().required())
        .min(1, 'Au moins une activité est obligatoire'),
    });

    const validate = (phoneObject: any) => {
      validPhone.value = phoneObject.valid ?? true;
    };

    const fetchCommunes = async () => {
      try {
        const { data } = await ApiService.get('/communes');
        console.log('Communes:', data.data);
        communeOptions.value = data.data.data.map((item: any) => ({
          value: item.id,
          label: item.libelle,
        }));
      } catch (err) {
        console.error('Erreur lors du chargement des communes:', err);
        error('Erreur lors du chargement des communes');
      }
    };

    const communeChange = async (value: number | null) => {
      quartierOptions.value = [];
      selectedQuartier.value = null;

      if (value !== null) {
        try {
          const { data } = await ApiService.get(`/communes/quartiers/${value}`);
          quartierOptions.value = data.data.map((quartier: any) => ({
            value: quartier.id,
            label: quartier.libelle,
          }));
        } catch (err) {
          console.error('Erreur lors du chargement des quartiers:', err);
          error('Erreur lors du chargement des quartiers');
        }
      }
    };

    const fetchMatiere = async () => {
      try {
        const { data } = await ApiService.get('/all/matieres');
        console.log('Matières:', data.data.data);
        matieresOptions.value = data.data.data.map((matiere: any) => ({
          value: matiere.id,
          label: matiere.libelle,
        }));
      } catch (err) {
        console.error('Erreur lors du chargement des matières:', err);
        error('Erreur lors du chargement des matières');
      }
    };

    const fetchLangue = async () => {
      try {
        const { data } = await ApiService.get('/langues');
        console.log('Languesdf:', data.data.data);
        languesOptions.value = data.data.data.map((langue: any) => ({
          value: langue.id,
          label: langue.libelle,
        }));
      } catch (err) {
        console.error('Erreur lors du chargement des langues:', err);
        error('Erreur lors du chargement des langues');
      }
    };


    const fetchQualification = async () => {
      try {
        const { data } = await ApiService.get('/all/qualifications');
        console.log('Qualifications:', data.data.data);
        qualificationOptions.value = data.data.data.map((qualification: any) => ({
          value: qualification.id,
          label: qualification.libelle,
        }));
      } catch (err) {
        console.error('Erreur lors du chargement des qualifications:', err);
        error('Erreur lors du chargement des qualifications');
      }
    };

    const fetchActivite = async () => {
      try {
        const { data } = await ApiService.get('/activites');
        console.log('Activités:', data.data.data);
        activiteOptions.value = data.data.data.map((activite: any) => ({
          value: activite.id,
          label: activite.libelle,
        }));
      } catch (err) {
        console.error('Erreur lors du chargement des activités:', err);
        error('Erreur lors du chargement des activités');
      }
    };


    const getProfesseur = async (id: number) => {
      try {
        const { data } = await ApiService.get(`/professeurs/${id}`);
        console.log('Données du professeur:', data.data);
        

        const professeurData = data.data;
        console.log("Réponse brute professeur :", JSON.stringify(professeurData, null, 2));

        initialValues.value = {
          nom: professeurData.nom || '',
          prenom: professeurData.prenom || '',
          npi: professeurData.npi || '',
          ifu: professeurData.ifu || '',
          email: professeurData.email || '',
          sexe: professeurData.sexe || '',
          telProfesseur1: professeurData.telProfesseur1 || '',
          telProfesseur2: professeurData.telProfesseur2 || '',
          dateNaissance: professeurData.dateNaissance
            ? professeurData.dateNaissance.split('T')[0]
            : '',
          nationalite: professeurData.nationalite || 'Bénin',
          commune: professeurData.quartier?.commune?.id || '',
          quartier: professeurData.quartier ? professeurData.quartier?.id : 0,
          anneeExperience: professeurData.anneeExperience || '',
          biographie: professeurData.biographie || '',
          categorie: professeurData.categorie || '',
          poste: professeurData.poste || '',
          matieres: professeurData.matieres?.map((m: any) => m.id) || [],
          langues: professeurData.langues || [],
          //langues: professeurData.langues?.map((m: any) => m.id) || [],
          
          qualification: professeurData.qualification?.id || '',
          activites: professeurData.activites?.map((a: any) => a.id) || [],
        };

        console.log('initialValues:', initialValues.value);
        console.log('commune vvr:', professeurData.quartier?.commune);
        console.log('arrondisement:', initialValues.value.quartier?.commune);
        console.log('commufffffffffffffffffffne:', initialValues.value.commune);

        if (professeurData.quartier?.arrondissement?.commune?.id) {

          await communeChange(professeurData.quartier.arrondissement.commune.id);
          initialValues.value.commune = professeurData.quartier.arrondissement.commune.id;
          selectedQuartier.value = professeurData.quartier.id;


        }

      } catch (err: any) {
        console.error('Erreur lors du chargement du professeur:', err);
        error(err.response?.data?.message || 'Erreur lors du chargement du professeur');
      }
    };

    const editProfesseur = async (values: any, { resetForm }: any) => {

      if (submitButton.value) submitButton.value.disabled = true;
      try {
        const id = route.params.id;
        values.commune = selectedCommune.value;
        values.quartier = selectedQuartier.value;

        await ApiService.put(`/professeurs/${id}`, values);

        success('Professeur modifié avec succès');
        resetForm();
        router.push({ name: 'ListeProfesseurPage' });
      } catch (err: any) {
        if (submitButton.value) submitButton.value.disabled = false;
        console.error('Erreur lors de la modification:', err);
        error(err.response?.data?.message || 'Erreur lors de la modification du professeur');
      }
    };

    onMounted(async () => {
      isLoading.value = true;
      console.log('ID from route:', route.params.id);
      await Promise.all([
        fetchCommunes(),
        fetchMatiere(),
        fetchLangue(),
        fetchQualification(),
        fetchActivite(),
      ]);
      if (route.params.id) {
        await getProfesseur(parseInt(route.params.id as string));

        // Après le chargement du professeur
        selectedCommune.value = initialValues.value.commune;
        await communeChange(selectedCommune.value); // Charger les quartiers

        selectedQuartier.value = initialValues.value.quartier;
      }

      isLoading.value = false;
    });

    return {
      isLoading,
      professeurSchema,
      initialValues,
      editProfesseur,
      currentDate,
      submitButton,
      validate,
      nationalite,
      countries,
      validPhone,
      communeOptions,
      quartierOptions,
      communeChange, selectedCommune,
      matieresOptions, selectedQuartier,
      qualificationOptions, languesOptions,
      activiteOptions,
    };
  },
});
</script>